import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

import SubscriptionModal from "./SubscriptionModal";
import Footer from './Footer2';

const ProfileCard = () => {
  const navigate = useNavigate();

  const [lessons, setLessons] = useState([]);
  const [quizUrl, setQuizUrl] = useState("");
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [error, setError] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // حالة جديدة لتخزين تقدم كل درس
  const [lessonProgress, setLessonProgress] = useState({});

  /* ===========================================================
     Fetch lessons
  ============================================================ */
  const fetchLessons = async () => {
    setLoading(true);
    try {
      const educationalLevelId = localStorage.getItem('package_id');
      const authToken = localStorage.getItem('authToken');

      if (!authToken || !educationalLevelId) {
        navigate('/Signin');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/lessons/package/${educationalLevelId}`,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      setLessons(response.data);

      // جلب تقدم كل درس بعد تحميل الدروس
      if (response.data.length) {
        response.data.forEach(lesson => {
          fetchLessonProgress(lesson.id);
        });
      }

      if (!response.data.length) {
        alert("لا يوجد محاضرات متاحة.");
      }

    } catch (err) {
      setError(err);
      alert("حدث خطأ أثناء تحميل المحاضرات.");
    } finally {
      setLoading(false);
    }
  };

  /* ===========================================================
     Fetch Lesson Progress
  ============================================================ */
  const fetchLessonProgress = async (lessonId) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/video-progress/${lessonId}`,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      setLessonProgress(prev => ({
        ...prev,
        [lessonId]: response.data
      }));

    } catch (err) {
      // إذا فشل الطلب، قد يكون الدرس غير مشاهد بعد
      setLessonProgress(prev => ({
        ...prev,
        [lessonId]: { percentage: 0, status: 'not_started' }
      }));
    }
  };

  /* ===========================================================
     Fetch Quiz URL
  ============================================================ */
  const fetchQuizData = async () => {
    try {
      const educationalLevelId = localStorage.getItem('package_id');
      const token = localStorage.getItem('authToken');

      if (!token || !educationalLevelId) return;

      const quizResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/mcqs/package/${educationalLevelId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const quizData = await quizResponse.json();

      if (quizData?.mcqs?.length > 0) {
        setQuizUrl(quizData.mcqs[0].url);
      }
    } catch (error) {
      console.error("Error fetching quiz:", error);
    }
  };

  /* ===========================================================
     Fetch Fingerprint
  ============================================================ */
  const fetchDeviceFingerprint = async () => {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();

      setDeviceFingerprint(result.visitorId);
      localStorage.setItem("deviceFingerprint", result.visitorId);

    } catch (err) {
      setErrorMessages(prev => ({ ...prev, global: 'Could not fetch device fingerprint' }));
      navigate('/Signin');
    }
  };

  /* ===========================================================
     Enter lesson
  ============================================================ */
  const handleEnterLesson = (lesson) => {
    localStorage.setItem('lesson_id', lesson.id);
    navigate('/VIDEOs');
  };

  /* ===========================================================
     Block DevTools
  ============================================================ */
  useEffect(() => {
    const handleKeydown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.key === 'u') ||
        (e.ctrlKey && e.shiftKey && e.key === 'I')
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleContextMenu = (e) => e.preventDefault();

    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  /* ===========================================================
     Resize (Mobile Mode)
  ============================================================ */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ===========================================================
     Load all on mount
  ============================================================ */
  useEffect(() => {
    fetchLessons();
    fetchQuizData();
    fetchDeviceFingerprint();
  }, []);

  return (
    <div style={{ padding: isMobile ? "15px" : "40px" }}>

      {/* 🔥 Animation definition inside JSX */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .progress-circle {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 16px;
          position: relative;
          background: rgba(0, 0, 0, 0.2);
        }
        
        .progress-circle::before {
          content: '';
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          border-radius: 50%;
          border: 2px solid currentColor;
          opacity: 0.3;
        }
      `}</style>

      {/* ======================= */}
      {/*       LESSON CARDS      */}
      {/* ======================= */}

      <div style={{ marginTop: 20 }}>
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p style={{ color: "white", fontSize: 24 }}>❌ لا يوجد محاضرات</p>
        ) : (
          lessons.map((lesson) => {
            const progress = lessonProgress[lesson.id];
            const percentage = progress?.percentage || 0;
            const status = progress?.status || 'not_started';

            // تحديد لون النسبة المئوية
            const percentageColor = percentage < 50 ? "#ff4757" : "#1e90ff";

            return (
              <div
                key={lesson.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  padding: "20px",
                  margin: "15px 0",
                  borderRadius: "18px",
                  backdropFilter: "blur(12px)",
                  background: "#ffc9a399",
                  border: "1px solid rgba(255,255,255,0.3)",
                  animation: "fadeUp 0.6s ease",
                  boxShadow: "0 0 12px rgba(255, 255, 255, 0.61)",
                  flexDirection: isMobile ? "column" : "row",
                  textAlign: isMobile ? "center" : "left",
                  position: "relative",
                }}
              >

                <img
                  src="/images/mo/logos.jpg"
                  alt="Lesson"
                  style={{
                    width: isMobile ? "60%" : "130px",
                    height: isMobile ? "60%" : "130px",
                    borderRadius: "15px",
                    objectFit: "cover",
                    boxShadow: "0 0 10px rgba(0,0,0,0.3)"
                  }}
                />

                <div style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px"
                }}>
                  <h3 style={{
                    fontSize: isMobile ? "20px" : "26px",
                    color: "black",
                    marginBottom: "5px",
                  }}>
                    {lesson.title}
                  </h3>

                  <p style={{
                    fontSize: isMobile ? "14px" : "17px",
                    color: "#eee",
                    marginBottom: "8px",
                  }}>
                    {lesson.description}
                  </p>

                  {/* Progress and Status Section */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                    marginBottom: "12px",
                    flexWrap: "wrap"
                  }}>
                    {/* Progress Circle */}
                    <div className="progress-circle" style={{ color: percentageColor }}>
                      {percentage}%
                    </div>

                    {/* Status */}
                    <div style={{
                      padding: "6px 12px",
                      borderRadius: "20px",
                      background: status === 'completed'
                        ? "rgba(46, 213, 116, 0.35)"
                        : status === 'in_progress'
                          ? "rgba(255, 166, 0, 0.44)"
                          : "rgba(243, 16, 16, 0.68)",
                      border: `1px solid ${status === 'completed'
                        ? "#2ed573"
                        : status === 'in_progress'
                          ? "#ffa502"
                          : "#dfe6e9"
                        }`,
                      color: status === 'completed'
                        ? "#2ed573"
                        : status === 'in_progress'
                          ? "#ffa502"
                          : "#dfe6e9",
                      fontSize: "14px",
                      fontWeight: "500"
                    }}>
                      {status === 'completed'
                        ? 'مكتمل'
                        : status === 'in_progress'
                          ? 'قيد التشغيل'
                          : 'لم يبدأ'}
                    </div>
                  </div>

                  <button
                    onClick={() => handleEnterLesson(lesson)}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "12px",
                      border: "none",
                      background: "linear-gradient(45deg,rgb(12, 155, 69),rgb(54, 19, 211))",
                      color: "white",
                      fontSize: "16px",
                      cursor: "pointer",
                      width: isMobile ? "100%" : "auto",
                      maxWidth: "200px",
                      boxShadow: "0 0 10px rgba(255,255,255,0.4)",
                      transition: "0.3s",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "scale(1.05)";
                      e.target.style.boxShadow = "0 0 15px rgba(255,255,255,0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "scale(1)";
                      e.target.style.boxShadow = "0 0 10px rgba(255,255,255,0.4)";
                    }}
                  >
                    دخول للمحاضرة
                  </button>
                </div>


              </div>
            );
          })
        )}
      </div>

      {/* QUIZ BUTTON */}
      <div style={{
        padding: 16,
        background: "#3a0239ff",
        borderRadius: 7,
        textAlign: "center",
        marginTop: 20
      }}>
        <a
          style={{
            color: "white",
            fontSize: isMobile ? "20px" : "24px",
            fontFamily: "Lemonada",
            textDecoration: "none",
            display: "inline-block",
            padding: "10px 20px",
            transition: "0.3s"
          }}
          href={quizUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!quizUrl) {
              e.preventDefault();
              alert("لم يتم تنزيل امتحان");
            }
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = "scale(1.05)";
            e.target.style.textShadow = "0 0 10px rgba(255,255,255,0.8)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "scale(1)";
            e.target.style.textShadow = "none";
          }}
        >
          الاختبار الشهري
        </a>
      </div>

      <Footer />
    </div>
  );
};

export default ProfileCard;